<template>
  <div class="crear-masivo flex">
    <!-- Sidebar de herramientas -->
    <aside class="sidebar flex flex-column">
      <div class="sidebar-header">
        <span class="font-bold">Crear varios territorios</span>
      </div>

      <div class="p-3 flex flex-column gap-3 overflow-auto flex-1">
        <Button
          label="Añadir territorio"
          icon="pi pi-plus"
          class="w-full"
          @click="addDraft"
        />

        <!-- Capas de referencia: lo que ya existe en la base de datos -->
        <div class="flex flex-column gap-1">
          <div class="flex gap-2">
            <Button
              label="Territorios"
              icon="pi pi-map"
              size="small"
              class="flex-1"
              severity="secondary"
              :outlined="!showTerritorios"
              @click="toggleTerritorios"
            />
            <Button
              label="P. encuentro"
              icon="pi pi-map-marker"
              size="small"
              class="flex-1"
              severity="secondary"
              :outlined="!showPuntos"
              @click="togglePuntos"
            />
          </div>
          <small class="text-color-secondary">
            Muestra como referencia los territorios y puntos de encuentro ya guardados.
          </small>
        </div>

        <p v-if="!drafts.length" class="text-sm text-color-secondary m-0">
          Añade un territorio y dibújalo en el mapa. Puedes crear varios a la vez.
        </p>

        <!-- Lista de territorios en edición -->
        <div class="flex flex-column gap-2">
          <div
            v-for="(d, i) in drafts"
            :key="d.uuid"
            class="draft-card p-2 border-round cursor-pointer"
            :class="{ active: i === activeIndex }"
            @click="selectDraft(i)"
          >
            <div class="flex align-items-center gap-2">
              <span class="swatch" :style="{ background: d.strokeColor }"></span>
              <span class="flex-1 text-sm">
                {{ (d.zona || '') + (d.numero ?? '') || 'Sin nombre' }}
              </span>
              <Button
                icon="pi pi-trash"
                text
                rounded
                severity="secondary"
                size="small"
                @click.stop="removeDraft(i)"
              />
            </div>
            <div class="text-xs text-color-secondary mt-1">
              {{ d.limits.length }} pts · {{ Math.max(d.mzNumbers.length - 1, 0) }} mz ·
              {{ d.puntoEncuentro.length }} pe
            </div>
          </div>
        </div>

        <!-- Edición del territorio activo -->
        <div v-if="active" class="flex flex-column gap-3 pt-3" style="border-top: 1px solid var(--separator)">
          <div class="flex gap-2">
            <div class="flex flex-column gap-1 flex-1">
              <label class="text-xs">Zona</label>
              <InputText v-model="active.zona" placeholder="A" class="w-full" />
            </div>
            <div class="flex flex-column gap-1 flex-1">
              <label class="text-xs">Número</label>
              <InputNumber v-model="active.numero" :min="1" :useGrouping="false" class="w-full" />
            </div>
          </div>
          <div class="flex flex-column gap-1">
            <label class="text-xs">Referencia</label>
            <InputText v-model="active.referencia" placeholder="Barrio / referencia" class="w-full" />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-xs">Color</label>
            <ColorPicker
              :modelValue="hexFromColor(active.strokeColor)"
              @update:modelValue="updateActiveColor"
            />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-xs">Modo de dibujo</label>
            <SelectButton
              v-model="mode"
              :options="modeOptions"
              optionLabel="label"
              optionValue="value"
              :allowEmpty="false"
            />
            <small class="text-color-secondary">{{ modeHint }}</small>
          </div>

          <div class="flex flex-column gap-2 pt-2" style="border-top: 1px solid var(--separator)">
            <Button
              label="Deshacer último punto"
              icon="pi pi-undo"
              outlined
              size="small"
              class="w-full"
              @click="undoLast"
            />

            <Button
              label="Quitar territorio"
              icon="pi pi-trash"
              outlined
              severity="danger"
              size="small"
              class="w-full"
              @click="removeDraft(activeIndex)"
            />
          </div>

          <!-- Edición de direcciones de los puntos de encuentro -->
          <div
            v-if="mode === 'pe' && active.puntoEncuentro.length"
            class="flex flex-column gap-2"
          >
            <label class="text-xs">Puntos de encuentro</label>
            <div
              v-for="(pe, pi) in active.puntoEncuentro"
              :key="pi"
              class="flex align-items-end gap-1"
            >
              <div class="flex flex-column gap-1 flex-1">
                <label class="text-xs text-color-secondary">P. encuentro {{ pi + 1 }}</label>
                <InputText v-model="pe.name" placeholder="Nombre descriptivo" class="w-full" />
                <InputText v-model="pe.direccion" placeholder="Dirección" class="w-full text-xs" />
              </div>
              <Button
                icon="pi pi-trash"
                text
                rounded
                severity="secondary"
                size="small"
                @click="removePE(pi)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer: acciones globales -->
      <div class="p-3 flex flex-column gap-2" style="border-top: 1px solid var(--separator)">
        <small v-if="statusMsg" :class="statusError ? 'text-red-500' : 'text-green-600'">
          {{ statusMsg }}
        </small>
        <Button
          label="Guardar todos"
          icon="pi pi-save"
          class="w-full"
          :loading="saving"
          :disabled="!drafts.length"
          @click="guardarTodos"
        />
        <Button
          label="Cancelar"
          severity="secondary"
          text
          class="w-full"
          @click="cancel"
        />
      </div>
    </aside>

    <!-- Mapa principal -->
    <div class="map-wrap flex-1">
      <MapaBase
        ref="mapBase"
        :editing="false"
        :saveCenter="false"
        @ready="onReady"
        @mapClick="onMapClick"
        @zoomend="redraw"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import ColorPicker from "primevue/colorpicker";
import MapaBase from "@/components/MapaBase/MapaBase.vue";
import { useRouterStore } from "@/store/router";
import { useTerritoriosStore } from "@/store/territorios";
import { useNominatim } from "@/composables/useNominatim";
import { useSaveTerritoriosMasivo } from "@/composables/commands/useSaveTerritoriosMasivo";
import { getContrastingColor } from "@/composables/color";
import uuid from "@/store/mix/uuid";

const route = useRouterStore();
const territorios = useTerritoriosStore();
const { reverseGeocode } = useNominatim();
const { execute: saveMasivo } = useSaveTerritoriosMasivo();

// Colores fuertes para distinguir cada territorio en el mapa
const PALETTE = [
  "#e6194B", "#3cb44b", "#4363d8", "#f58231", "#911eb4",
  "#008080", "#f032e6", "#9A6324", "#808000", "#000075",
];

// Persistencia en localStorage: si se recarga la página, se recupera el trabajo en curso
const STORAGE_KEY = "territorios-masivo-drafts-v1";

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistDrafts = () => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ drafts: drafts.value, activeIndex: activeIndex.value, mode: mode.value })
    );
  } catch {
    /* localStorage no disponible */
  }
};

const clearPersisted = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage no disponible */
  }
};

const persisted = loadPersisted();

const drafts = ref(persisted?.drafts || []);
const activeIndex = ref(persisted?.activeIndex ?? -1);
const mode = ref(persisted?.mode || "limits");
const saving = ref(false);
const statusMsg = ref("");
const statusError = ref(false);

watch([drafts, activeIndex, mode], persistDrafts, { deep: true });

// Capas de referencia (territorios y puntos de encuentro ya guardados)
const showTerritorios = ref(false);
const showPuntos = ref(false);

const toggleTerritorios = () => {
  showTerritorios.value = !showTerritorios.value;
  redraw();
};

const togglePuntos = () => {
  showPuntos.value = !showPuntos.value;
  redraw();
};

const mapBase = ref(null);
const mapReady = ref(false);
let activeLimitIndex = null;

const modeOptions = [
  { label: "Límites", value: "limits" },
  { label: "Manzanas", value: "manzanas" },
  { label: "P. encuentro", value: "pe" },
];

const active = computed(() => drafts.value[activeIndex.value] || null);

const modeHint = computed(() => {
  if (mode.value === "limits") return "Haz clic para añadir vértices del polígono.";
  if (mode.value === "manzanas") return "El primer punto es el centro; luego añade manzanas.";
  return "Haz clic para añadir puntos de encuentro.";
});

const m = () => mapBase.value?.m;
const map = () => mapBase.value?.m?.map?.value;

// ---- Drafts ----
const addDraft = async () => {
  const id = await uuid.new();
  const color = PALETTE[drafts.value.length % PALETTE.length];
  drafts.value.push({
    uuid: id,
    zona: "",
    numero: null,
    referencia: "",
    notas: "",
    strokeColor: color,
    color: { backgroundColor: color, color: "#ffffff" },
    center: null,
    limits: [],
    mzNumbers: [],
    puntoEncuentro: [],
    tipo: "default",
  });
  activeIndex.value = drafts.value.length - 1;
  mode.value = "limits";
  activeLimitIndex = null;
  redraw();
};

const selectDraft = (i) => {
  activeIndex.value = i;
  activeLimitIndex = null;
  redraw();
};

const removeDraft = (i) => {
  drafts.value.splice(i, 1);
  if (activeIndex.value >= drafts.value.length) activeIndex.value = drafts.value.length - 1;
  activeLimitIndex = null;
  redraw();
};

// ---- Color ----
const hexFromColor = (color) => {
  if (!color) return "ffffff";
  if (color.startsWith("#")) return color.slice(1);
  const nums = color.match(/\d+/g);
  if (nums) {
    return nums.slice(0, 3).map((n) => parseInt(n).toString(16).padStart(2, "0")).join("");
  }
  return "ffffff";
};

const updateActiveColor = (hex) => {
  const d = active.value;
  if (!hex || !d) return;
  const color = "#" + hex;
  d.strokeColor = color;
  d.color = { backgroundColor: color, color: getContrastingColor(hex) };
  redraw();
};

// ---- Interacción con el mapa ----
const onMapClick = (e) => {
  const d = active.value;
  if (!d) return;
  if (mode.value === "limits") addLimit(d, e);
  else if (mode.value === "manzanas") addManzana(d, e);
  else if (mode.value === "pe") addPE(d, e);
};

const addLimit = (d, e) => {
  const ll = [e.latlng.lat, e.latlng.lng];
  if (activeLimitIndex !== null) {
    d.limits.splice(activeLimitIndex + 1, 0, ll);
    activeLimitIndex = activeLimitIndex + 1;
  } else {
    d.limits.push(ll);
  }
  redraw();
};

const addManzana = (d, e) => {
  d.mzNumbers.push([e.latlng, d.mzNumbers.length]);
  redraw();
};

const addPE = async (d, e) => {
  const latlng = e.latlng;
  // Inserta de inmediato para no perder el orden de clics; rellena la dirección luego.
  d.puntoEncuentro.push({ name: "", direccion: "", latlng });
  const idx = d.puntoEncuentro.length - 1;
  redraw();
  try {
    const address = await reverseGeocode(latlng);
    const a = address?.address;
    // Accedemos a través del array reactivo para que Vue detecte el cambio
    d.puntoEncuentro[idx].direccion =
      (a?.house_number ? `${a.road} ${a.house_number}`.trim() : null) ??
      address?.intersection ??
      a?.road ??
      address?.name ??
      address?.display_name?.split(",")[0] ??
      "";
  } catch {
    /* sin dirección */
  }
};

const removePE = (pi) => {
  const d = active.value;
  if (!d) return;
  d.puntoEncuentro.splice(pi, 1);
  redraw();
};

const undoLast = () => {
  const d = active.value;
  if (!d) return;
  if (mode.value === "limits") {
    d.limits.pop();
    activeLimitIndex = null;
  } else if (mode.value === "manzanas") {
    d.mzNumbers.pop();
  } else if (mode.value === "pe") {
    d.puntoEncuentro.pop();
  }
  redraw();
};

// ---- Render en Leaflet ----
let layers = [];

const clearLayers = () => {
  const mp = map();
  if (!mp) return;
  layers.forEach((l) => {
    try {
      mp.removeLayer(l);
    } catch {
      /* capa ya removida */
    }
  });
  layers = [];
};

const onReady = () => {
  mapReady.value = true;
  redraw();
};

// Dibuja, con el color propio de cada territorio, los ya guardados como referencia
const drawReferenceTerritorios = (mp, L) => {
  territorios.list.forEach((t) => {
    const bg = t.color?.backgroundColor || "#6b7280";
    const fg = t.color?.color || "#000000";

    if (Array.isArray(t.limits) && t.limits.length > 1) {
      const poly = L.polygon(t.limits, {
        color: bg,
        weight: 2,
        opacity: 0.9,
        fillColor: bg,
        fillOpacity: 0.25,
        interactive: false,
      }).addTo(mp);
      layers.push(poly);
    }

    // Etiqueta con el nombre del territorio en su centro, con sus propios colores
    const center = t.center || t.mzNumbers?.[0]?.[0];
    const label = `${t.zona || ""}${t.numero ?? ""}`;
    if (center && label) {
      const icon = L.divIcon({
        className: "ref-territorio-label",
        iconSize: null,
        html: `<div class="ref-label" style="background:${bg};color:${fg};border-color:${bg};">${label}</div>`,
      });
      const marker = L.marker(center, { icon, interactive: false }).addTo(mp);
      layers.push(marker);
    }
  });
};

// Dibuja todos los puntos de encuentro ya guardados como referencia:
// los de cada territorio + los configurados en los ajustes
const drawReferencePuntos = (mp, L) => {
  const icon = L.icon({
    iconUrl: "/pe.png",
    iconSize: [28, 28],
    alt: "Punto de Encuentro",
  });

  const addPe = (pe) => {
    if (!pe?.latlng) return;
    const marker = L.marker(pe.latlng, {
      icon,
      interactive: true,
      opacity: 0.75,
    }).addTo(mp);
    const tip = `${pe.name || ""} ${pe.direccion || ""}`.trim();
    if (tip) marker.bindTooltip(tip);
    layers.push(marker);
  };

  // Puntos de encuentro de cada territorio
  territorios.list.forEach((t) => (t.puntoEncuentro || []).forEach(addPe));

  // Puntos de encuentro configurados en los ajustes
  (territorios.puntoEncuentro || []).forEach(addPe);
};

const redraw = () => {
  if (!mapReady.value) return;
  const mp = map();
  if (!mp) return;
  const L = m().L;
  clearLayers();

  // Capas de referencia (debajo de los drafts en edición)
  if (showTerritorios.value) drawReferenceTerritorios(mp, L);
  if (showPuntos.value) drawReferencePuntos(mp, L);

  drafts.value.forEach((d, di) => {
    const isActive = di === activeIndex.value;

    // Polígono de límites
    if (d.limits.length > 0) {
      const poly = L.polyline([...d.limits, d.limits[0]], {
        color: d.strokeColor,
        weight: isActive ? 4 : 3,
        opacity: isActive ? 0.9 : 0.4,
        smoothFactor: 1,
      }).addTo(mp);
      layers.push(poly);
    }

    // Vértices arrastrables (solo el territorio activo es editable)
    if (isActive) {
      const markerIcon = L.icon({
        iconUrl: "marker-icon.png",
        iconAnchor: [12, 41],
        iconSize: [25, 41],
      });
      d.limits.forEach((pt, vi) => {
        const marker = L.marker({ lat: pt[0], lng: pt[1] }, {
          icon: markerIcon,
          draggable: true,
        }).addTo(mp);
        marker.on("dragend", (ev) => {
          const ll = ev.target.getLatLng();
          d.limits[vi] = [ll.lat, ll.lng];
          redraw();
        });
        marker.on("click", () => {
          activeLimitIndex = vi;
        });
        marker.on("dblclick", () => {
          d.limits.splice(vi, 1);
          activeLimitIndex = null;
          redraw();
        });
        layers.push(marker);
      });
    }

    // Manzanas (índice 0 = centro, muestra el nombre del territorio)
    d.mzNumbers.forEach((mz, mi) => {
      const isCenter = mi === 0;
      const label = isCenter ? (d.zona || "") + (d.numero ?? "") : String(mi);
      const inlineColors = isCenter
        ? ""
        : `color:${d.color.color};background:${d.color.backgroundColor};border-color:${d.color.backgroundColor};`;
      const icon = L.divIcon({
        className: "manzana-marker ",
        iconSize: null,
        html: `<div class="icon ${isCenter ? "manzana-marker-center" : ""}" style="font-size:1em; ${inlineColors}">${label}</div><div class="arrow"></div>`,
      });
      const marker = L.marker(mz[0], { icon, draggable: isActive }).addTo(mp);
      if (isActive) {
        marker.on("dragend", (ev) => {
          d.mzNumbers[mi] = [ev.target.getLatLng(), mi];
          redraw();
        });
        marker.on("dblclick", () => {
          d.mzNumbers.splice(mi, 1);
          d.mzNumbers = d.mzNumbers.map((x, idx) => [x[0], idx]);
          redraw();
        });
      }
      layers.push(marker);
    });

    // Puntos de encuentro
    d.puntoEncuentro.forEach((pe, pi) => {
      const icon = L.icon({
        iconUrl: "/pe.png",
        iconSize: [32, 32],
        alt: "Punto de Encuentro",
      });
      const marker = L.marker(pe.latlng, { icon, draggable: isActive }).addTo(mp);
      if (isActive) {
        marker.on("dragend", (ev) => {
          pe.latlng = ev.target.getLatLng();
          redraw();
        });
        marker.on("dblclick", () => {
          d.puntoEncuentro.splice(pi, 1);
          redraw();
        });
      }
      layers.push(marker);
    });
  });
};

// ---- Guardar ----
const guardarTodos = async () => {
  if (!drafts.value.length) return;
  saving.value = true;
  statusMsg.value = "";
  statusError.value = false;
  const zoom = mapReady.value ? Math.round(map().getZoom()) : 17;
  const res = await saveMasivo(drafts.value, { zoom });
  saving.value = false;

  if (res.errors.length) {
    statusError.value = true;
    statusMsg.value = `${res.saved.length} guardados, ${res.skipped.length} omitidos, ${res.errors.length} con error.`;
    return;
  }
  if (res.skipped.length && !res.saved.length) {
    statusError.value = true;
    statusMsg.value = "Cada territorio necesita número y al menos 3 puntos de límite.";
    return;
  }
  clearPersisted();
  route.$patch({ page: "listaTerritorio" });
};

const cancel = () => {
  clearPersisted();
  route.$patch({ page: "listaTerritorio" });
};

onUnmounted(() => {
  clearLayers();
});
</script>

<style scoped>
.crear-masivo {
  height: 100vh;
  overflow: hidden;
}
.sidebar {
  width: 320px;
  min-width: 320px;
  height: 100vh;
  background: var(--bg-primary);
  box-shadow: var(--shadow-2);
  z-index: 1001;
}
.sidebar-header {
  padding: 12px;
  background: var(--accent);
  color: #fff;
}
.map-wrap {
  position: relative;
}
.draft-card {
  border: 1px solid var(--separator);
  background: var(--fill-quaternary);
}
.draft-card.active {
  border-color: var(--accent);
  background: var(--accent-soft);
}
.swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
  flex: 0 0 auto;
}
</style>

<style>
/* Etiqueta de los territorios de referencia (no scoped: el divIcon vive fuera del componente) */
.ref-territorio-label .ref-label {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transform: translate(-50%, -50%);
  pointer-events: none;
}
</style>
